import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Progress,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  MailOutlined,
  TeamOutlined,
  UserOutlined,
  WindowsOutlined,
} from "@ant-design/icons";
import { initiateLinkedInWebSignIn } from "../api/authApi";
import apiClient from "utils/apiClient";
import AuthShell from "components/auth/AuthShell";
import AuthBrand from "components/auth/AuthBrand";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import { clearPostLoginRedirect } from "utils/postLoginRedirect";
import { OLIVEEB_COLORS } from "workspaces/colors";
import { createWorkspace, fetchMemberWorkspaces } from "services/workspaceService";
import olreadiaService from "services/olreadiaService";
import {
  assertAppleRedirectUri,
  getAppleClientId,
  getAppleRedirectUri,
} from "utils/appleAuth";

const { Paragraph, Text, Title } = Typography;

const starterStorageKey = "olreadiaInitialWorkspaceDraft";
const globalSelectedWorkspaceKey = "selectedWorkspaceId";
const globalSelectedWorkspaceReadyKey = "selectedWorkspaceReady";
const selectedWorkspaceKeyFor = (userId) => `selectedWorkspaceId:${userId || "anonymous"}`;

const steps = [
  {
    key: "account",
    title: "Choose account type",
    reason: "Select whether this account is for you or for an organization.",
  },
  {
    key: "intent",
    title: "How will you use Olreadia first?",
    reason: "Choose the first experience we should prepare for you.",
  },
  {
    key: "credentials",
    title: "Create your login",
    reason: "Use email and password, or choose a social account for faster setup.",
  },
  {
    key: "name",
    title: "What is your name?",
    reason: "Add the first and last name for the person managing this account.",
  },
];

const accountTypeOptions = [
  {
    value: "professional",
    title: "Individual",
    description: "I am creating an account for myself.",
    icon: <UserOutlined />,
    color: OLIVEEB_COLORS.oliveebBlue,
    soft: OLIVEEB_COLORS.oliveebBlueSoft,
  },
  {
    value: "hiring_organization",
    title: "Hiring organization",
    description: "I am creating an account for a company or team.",
    icon: <TeamOutlined />,
    color: OLIVEEB_COLORS.oliveebGreen,
    soft: OLIVEEB_COLORS.oliveebGreenSoft,
  },
];

const helpOptions = [
  {
    value: "verified_profile",
    title: "Build a trusted profile",
    description: "Prepare reusable profile details employers can trust.",
    icon: <UserOutlined />,
    color: OLIVEEB_COLORS.oliveebBlue,
    soft: OLIVEEB_COLORS.oliveebBlueSoft,
  },
  {
    value: "find_verified_talent",
    title: "Find verified talent",
    description: "Set up hiring context for reviewing professionals faster.",
    icon: <TeamOutlined />,
    color: OLIVEEB_COLORS.oliveebGreen,
    soft: OLIVEEB_COLORS.oliveebGreenSoft,
  },
  {
    value: "background_check",
    title: "Background checks",
    description: "Organize verification details before a role needs them.",
    icon: <CheckCircleOutlined />,
    color: OLIVEEB_COLORS.oliveebOrange,
    soft: OLIVEEB_COLORS.goldSoft,
  },
];

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return {};

  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return {};
    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
};

const splitName = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

const accountTypeToIntent = (accountType) =>
  accountType === "hiring_organization" ? "find_verified_talent" : "verified_profile";

const personalWorkspaceName = (allValues = {}) => {
  const displayName = [allValues.firstName, allValues.lastName]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

  return displayName ? `${displayName}'s workspace` : "Personal job search workspace";
};

const workspaceNameFor = ({ accountType, organizationName, workspaceUse, allValues }) => {
  if (accountType === "ORGANIZATION") return organizationName;
  if (workspaceUse === "HIRING") return "Personal hiring workspace";
  return personalWorkspaceName(allValues);
};

const providerLabel = (provider) =>
  ({
    google: "Google",
    facebook: "Facebook",
    apple: "Apple",
    microsoft: "Microsoft",
    linkedin: "LinkedIn",
  })[provider] || "Social";

const canCompleteSocialSignup = (result) => {
  const details = result?.details || {};
  return Boolean(
    details?.requiresPassword ||
    details?.requiresLinkApproval ||
    details?.userInfo ||
    result?.status === 201,
  );
};

const buildAppleAuthPayload = (appleResponse = {}) => {
  const authorization = appleResponse?.authorization || {};
  const identityToken =
    authorization.id_token ||
    authorization.idToken ||
    appleResponse?.identityToken ||
    appleResponse?.id_token ||
    appleResponse?.idToken ||
    "";
  const authorizationCode = authorization.code || appleResponse?.code || "";
  const jwtPayload = decodeJwtPayload(identityToken);
  const appleUser = appleResponse?.user;
  const appleUserId =
    (typeof appleUser === "string" ? appleUser : "") ||
    appleResponse?.userId ||
    jwtPayload.sub ||
    "";
  const appleProfile = typeof appleUser === "object" && appleUser ? appleUser : {};
  const appleName = appleProfile.name || {};
  const firstName = appleName.firstName || appleName.givenName || jwtPayload.given_name || "";
  const lastName = appleName.lastName || appleName.familyName || jwtPayload.family_name || "";
  const email = appleProfile.email || appleResponse?.email || jwtPayload.email || "";

  return {
    authPayload: {
      identityToken,
      idToken: identityToken,
      authorizationCode,
      code: authorizationCode,
      user: appleUserId,
      appleUserId,
      providerUserId: appleUserId,
      fullName:
        firstName || lastName
          ? {
              givenName: firstName,
              familyName: lastName,
            }
          : undefined,
      email,
      redirectUri: APPLE_REDIRECT_URI,
    },
    profile: {
      email,
      firstName,
      lastName,
    },
  };
};

const clearStarterDraft = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(starterStorageKey);
  } catch {
    // Local storage cleanup is best-effort.
  }
};

const clearWorkspaceSelectionCache = () => {
  if (typeof window === "undefined") return;
  try {
    const keysToRemove = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (
        key === globalSelectedWorkspaceKey ||
        key?.startsWith("selectedWorkspaceId:") ||
        key?.startsWith("selectedWorkspaceUse:")
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
    window.localStorage.setItem(globalSelectedWorkspaceReadyKey, "1");
  } catch {
    // Workspace cache cleanup is best-effort.
  }
};

const wait = (durationMs) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, durationMs);
  });

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || "822618703980688";
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || "366258d5-f9f5-4591-81eb-22976b18775f";
const GOOGLE_SDK_SRC = "https://accounts.google.com/gsi/client";
const FACEBOOK_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const MICROSOFT_SDK_SRC =
  "https://cdn.jsdelivr.net/npm/@azure/msal-browser@2.38.3/lib/msal-browser.min.js";
const MICROSOFT_SDK_SRC_FALLBACK =
  "https://unpkg.com/@azure/msal-browser@2.38.3/lib/msal-browser.min.js";
const APPLE_CLIENT_ID = getAppleClientId();
const APPLE_REDIRECT_URI = assertAppleRedirectUri(getAppleRedirectUri());
const APPLE_SDK_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

const compactInputStyle = {
  height: 54,
  borderRadius: 999,
  borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
  background: "#fff",
  boxShadow: "0 14px 28px rgba(16,147,87,0.10)",
  color: OLIVEEB_COLORS.oliveebDarkBlue,
  fontSize: 17,
  fontWeight: 800,
};

const compactFormItemStyle = {
  marginBottom: 10,
};

const primaryActionStyle = {
  height: 50,
  borderRadius: 999,
  fontWeight: 900,
  fontSize: 16,
  background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen} 0%, #19B86D 100%)`,
  borderColor: OLIVEEB_COLORS.oliveebGreen,
  boxShadow: "0 12px 24px rgba(16, 147, 87, 0.22)",
};

const formLabelStyle = `
  .olreadia-signup-form .ant-form-item-label > label {
    color: ${OLIVEEB_COLORS.oliveebGreen};
    font-size: 13px;
    font-weight: 900;
  }
  .olreadia-signup-form .ant-input,
  .olreadia-signup-form .ant-input-affix-wrapper,
  .olreadia-signup-form .ant-select-selector {
    border-radius: 999px !important;
    font-size: 17px;
    font-weight: 800;
    background: #fff !important;
    color: ${OLIVEEB_COLORS.oliveebDarkBlue};
  }
  .olreadia-signup-form .ant-input-affix-wrapper {
    overflow: hidden;
  }
  .olreadia-signup-form .ant-form-item-control-input-content {
    filter: drop-shadow(0 8px 14px rgba(16,147,87,0.10));
  }
  .olreadia-signup-form .ant-input-affix-wrapper-focused,
  .olreadia-signup-form .ant-input-affix-wrapper:focus,
  .olreadia-signup-form .ant-select-focused .ant-select-selector {
    border-color: ${OLIVEEB_COLORS.oliveebGreen} !important;
    box-shadow: 0 0 0 4px rgba(16, 147, 87, 0.12) !important;
  }
  .olreadia-choice-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
  .olreadia-choice-grid.help {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .olreadia-signup-social {
    width: 100%;
    max-width: 390px;
    margin: 0 auto 12px;
  }
  .olreadia-signup-social .auth-social__card {
    display: grid;
    gap: 12px;
    justify-items: center;
    padding: 12px;
    border-radius: 22px;
    border: 1px solid ${OLIVEEB_COLORS.oliveebGreenSoft};
    background: linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(246,255,249,0.92) 100%);
    box-shadow: 0 16px 32px rgba(16,147,87,0.10);
  }
  .olreadia-signup-social .auth-social__google,
  .olreadia-signup-social .auth-social__provider,
  .olreadia-signup-social .auth-social__apple {
    width: 100%;
    max-width: 330px;
    min-height: 48px;
    border-radius: 999px !important;
    font-weight: 900 !important;
    box-shadow: 0 10px 20px rgba(15,23,42,0.08);
  }
  .olreadia-signup-social .auth-social__microsoft {
    border-color: #D7DEE9 !important;
    color: ${OLIVEEB_COLORS.oliveebDarkBlue} !important;
  }
  @media (max-width: 720px) {
    .olreadia-choice-grid,
    .olreadia-choice-grid.help {
      grid-template-columns: 1fr;
    }
    .olreadia-signup-social {
      max-width: 100%;
    }
  }
`;

let googleSdkPromise = null;
let facebookSdkPromise = null;
let appleSdkPromise = null;
let microsoftSdkPromise = null;

const AppleBrandIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const FacebookBrandIcon = () => (
  <span style={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }}>f</span>
);

const LinkedInBrandIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const loadScript = (src, promiseRefSetter) => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Social sign-up is only available in the browser."));
  }

  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load social sign-up.")), { once: true });
      if (existing.dataset.loaded === "true") resolve();
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => {
      promiseRefSetter(null);
      reject(new Error("Failed to load social sign-up."));
    };
    document.body.appendChild(script);
  });
};

const loadGoogleSdk = () => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!googleSdkPromise) {
    googleSdkPromise = loadScript(GOOGLE_SDK_SRC, (value) => {
      googleSdkPromise = value;
    });
  }
  return googleSdkPromise;
};

const loadAppleSdk = () => {
  if (window.AppleID?.auth) return Promise.resolve();
  if (!appleSdkPromise) {
    appleSdkPromise = loadScript(APPLE_SDK_SRC, (value) => {
      appleSdkPromise = value;
    });
  }
  return appleSdkPromise;
};

const loadFacebookSdk = () => {
  if (window.FB) return Promise.resolve();
  if (!facebookSdkPromise) {
    facebookSdkPromise = loadScript(FACEBOOK_SDK_SRC, (value) => {
      facebookSdkPromise = value;
    }).then(() => {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: "v19.0",
      });
    });
  }
  return facebookSdkPromise;
};

const loadMicrosoftSdk = (useFallback = false) => {
  const scriptSrc = useFallback ? MICROSOFT_SDK_SRC_FALLBACK : MICROSOFT_SDK_SRC;
  const sourceLabel = useFallback ? "fallback" : "primary";
  if (window.msal?.PublicClientApplication) return Promise.resolve();
  const existing = document.querySelector(`script[src="${scriptSrc}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50;
      const interval = setInterval(() => {
        if (window.msal?.PublicClientApplication) {
          clearInterval(interval);
          resolve();
        } else if (++attempts >= maxAttempts) {
          clearInterval(interval);
          if (!useFallback) {
            microsoftSdkPromise = null;
            loadMicrosoftSdk(true).then(resolve).catch(reject);
          } else {
            reject(new Error(`Microsoft SDK (${sourceLabel}) loaded but msal.PublicClientApplication not available.`));
          }
        }
      }, 100);
    });
  }
  if (!microsoftSdkPromise) {
    microsoftSdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setTimeout(() => {
          let attempts = 0;
          const maxAttempts = 50;
          const interval = setInterval(() => {
            if (window.msal?.PublicClientApplication) {
              clearInterval(interval);
              resolve();
            } else if (++attempts >= maxAttempts) {
              clearInterval(interval);
              if (!useFallback) {
                microsoftSdkPromise = null;
                loadMicrosoftSdk(true).then(resolve).catch(reject);
              } else {
                reject(new Error(`Microsoft SDK (${sourceLabel}) loaded but msal.PublicClientApplication not available.`));
              }
            }
          }, 100);
        }, 100);
      };
      script.onerror = () => {
        if (!useFallback) {
          microsoftSdkPromise = null;
          loadMicrosoftSdk(true).then(resolve).catch(reject);
        } else {
          reject(new Error(`Failed to load Microsoft SDK from ${sourceLabel} source.`));
        }
      };
      document.body.appendChild(script);
    });
  }
  return microsoftSdkPromise;
};

const GoogleSignupButton = ({ disabled, onSuccess, onError }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const renderButton = async () => {
      try {
        if (!GOOGLE_CLIENT_ID) throw new Error("Google sign-up is not configured.");
        await loadGoogleSdk();
        if (cancelled || !containerRef.current || !window.google?.accounts?.id) return;

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => {
            if (response?.credential) onSuccess(response.credential);
            else onError(new Error("No Google credential returned."));
          },
        });
        containerRef.current.innerHTML = "";
        window.google.accounts.id.renderButton(containerRef.current, {
          theme: "outline",
          size: "large",
          text: "signup_with",
          shape: "pill",
          width: 300,
          logo_alignment: "left",
        });
      } catch (error) {
        onError(error);
      }
    };

    renderButton();
    return () => {
      cancelled = true;
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [onError, onSuccess]);

  return (
    <div
      ref={containerRef}
      className="auth-social__google"
      style={{
        width: "100%",
        maxWidth: 300,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    />
  );
};

const ChoiceCard = ({ option, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: "100%",
      minHeight: 98,
      textAlign: "left",
      borderRadius: 20,
      border: `1px solid ${selected ? option.color : "#DCEBE4"}`,
      background: selected
        ? `linear-gradient(145deg, #FFFFFF 0%, ${option.soft} 100%)`
        : "linear-gradient(145deg, #FFFFFF 0%, #F8FCFA 100%)",
      boxShadow: selected
        ? `0 18px 32px ${option.color}22`
        : "0 12px 24px rgba(16,147,87,0.08)",
      padding: 14,
      cursor: "pointer",
      color: OLIVEEB_COLORS.textPrimary,
    }}
  >
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: option.color,
          background: "#fff",
          boxShadow: `0 8px 18px ${option.color}22`,
          fontSize: 18,
        }}
      >
        {option.icon}
      </span>
      <Text style={{ display: "block", color: option.color, fontWeight: 900, lineHeight: 1.2 }}>
        {option.title}
      </Text>
      <Text style={{ display: "block", color: OLIVEEB_COLORS.textMuted, fontSize: 12, lineHeight: 1.35 }}>
        {option.description}
      </Text>
    </Space>
  </button>
);

const AuthSignup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    user,
    isAuthenticated,
    loginWithSocialProvider,
  } = useAuth();
  const { refreshWorkspaces, selectWorkspace } = useWorkspace();
  const [form] = Form.useForm();
  const [stepIndex, setStepIndex] = useState(0);
  const [stepHistory, setStepHistory] = useState([0]);
  const [loading, setLoading] = useState(false);
  const [preparingAccount, setPreparingAccount] = useState(false);
  const [pendingWorkspaceSetup, setPendingWorkspaceSetup] = useState(null);
  const [workspaceSetupBlocked, setWorkspaceSetupBlocked] = useState(false);
  const [socialLoading, setSocialLoading] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [socialProvider, setSocialProvider] = useState("");
  const [socialData, setSocialData] = useState(null);
  const signupLaunchHandledRef = useRef(false);
  const currentStep = steps[stepIndex];
  const isSocialSignup = Boolean(socialProvider);
  const onboardingDraft = useMemo(() => {
    if (location.state?.onboardingDraft) return location.state.onboardingDraft;
    try {
      return JSON.parse(window.localStorage.getItem(starterStorageKey) || "null");
    } catch {
      return null;
    }
  }, [location.state?.onboardingDraft]);
  const starterCredentialMethod =
    location.state?.signupCredentialMethod ||
    (onboardingDraft?.credentialMethod === "social_login"
      ? "social"
      : onboardingDraft?.credentialMethod === "username_password"
        ? "form"
        : "");
  const moveToStep = useCallback((nextIndex, { replace = false } = {}) => {
    const normalizedIndex = Math.max(0, Math.min(nextIndex, steps.length - 1));
    setStepIndex(normalizedIndex);
    setStepHistory((history) => {
      if (replace) return [normalizedIndex];
      if (history[history.length - 1] === normalizedIndex) return history;
      return [...history, normalizedIndex];
    });
  }, []);

  useEffect(() => {
    if (!onboardingDraft) return;
    try {
      window.localStorage.setItem(starterStorageKey, JSON.stringify(onboardingDraft));
    } catch {
      // Route state still keeps the starter context for this signup session.
    }
  }, [onboardingDraft]);

  useEffect(() => {
    if (!onboardingDraft || location.state?.socialDraft) return;
    const accountType =
      onboardingDraft.workspaceCreation?.accountType === "ORGANIZATION" ||
      onboardingDraft.usageType === "hiring_team"
        ? "hiring_organization"
        : "professional";
    const draftHelpFirst =
      onboardingDraft.workspaceCreation?.workspaceUse === "HIRING"
        ? "find_verified_talent"
        : Array.isArray(onboardingDraft.deliveryFocus)
          ? onboardingDraft.deliveryFocus[0]
          : onboardingDraft.deliveryFocus || accountTypeToIntent(accountType);
    form.setFieldsValue({
      helpFirst: draftHelpFirst,
      accountType,
      organizationName: accountType === "hiring_organization" ? onboardingDraft.workspaceName || "" : "",
      firstName: accountType === "professional" ? onboardingDraft.firstName || "" : "",
      lastName: accountType === "professional" ? onboardingDraft.lastName || "" : "",
      email: onboardingDraft.email || "",
    });
  }, [form, location.state?.socialDraft, onboardingDraft]);

  useEffect(() => {
    if (!onboardingDraft || location.state?.socialDraft || signupLaunchHandledRef.current) return;
    const credentialMethod = starterCredentialMethod;
    if (!credentialMethod) return;

    signupLaunchHandledRef.current = true;
    if (credentialMethod === "form" && location.state?.signupCredentials) {
      const signupCredentials = location.state.signupCredentials;
      form.setFieldsValue({
        email: signupCredentials.email || onboardingDraft.email || "",
        password: signupCredentials.password || "",
        confirmPassword: signupCredentials.password || "",
      });
      moveToStep(steps.findIndex((step) => step.key === "name"), { replace: true });
      return;
    }

    moveToStep(steps.findIndex((step) => step.key === "credentials"), { replace: true });
  }, [
    form,
    moveToStep,
    onboardingDraft,
    location.state?.signupCredentials,
    starterCredentialMethod,
    location.state?.signupSocialProvider,
    location.state?.socialDraft,
  ]);

  useEffect(() => {
    const draft = location.state?.socialDraft;
    if (!draft?.provider || !draft?.authPayload) return;

    setSocialProvider(draft.provider);
    setSocialData({
      authPayload: draft.authPayload,
      details: draft.details || {},
      userInfo: draft.userInfo || {},
    });
    form.setFieldsValue({
      email: draft.prefill?.email || draft.userInfo?.email || "",
      firstName: draft.prefill?.firstName || draft.userInfo?.firstName || "",
      lastName: draft.prefill?.lastName || draft.userInfo?.lastName || "",
    });
    setSubmitError(
      draft.details?.message ||
      `${providerLabel(draft.provider)} verified. Complete these details to finish your Olreadia account.`,
    );
    moveToStep(steps.findIndex((step) => step.key === "name"), { replace: true });
  }, [form, location.state?.socialDraft, moveToStep]);

  useEffect(() => {
    if (isAuthenticated && !loading && !preparingAccount && !onboardingDraft && !workspaceSetupBlocked) {
      navigate(paths.pmDashboard, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, onboardingDraft, preparingAccount, workspaceSetupBlocked]);

  const values = Form.useWatch([], form) || {};
  const selectedAccountType = values.accountType || "professional";
  const socialOnlyCredentialStep =
    currentStep.key === "credentials" &&
    starterCredentialMethod === "social" &&
    !isSocialSignup;
  const signupProgress = Math.round(((stepIndex + 1) / steps.length) * 100);
  const stepTitle = socialOnlyCredentialStep ? "Choose your social login" : currentStep.title;
  const stepReason = socialOnlyCredentialStep
    ? "Continue with one of these providers."
    : currentStep.reason;

  const getSignupIdentity = (allValues = form.getFieldsValue(true)) => {
    const organizationName =
      (allValues.accountType || "professional") === "hiring_organization"
        ? String(allValues.organizationName || "").trim()
        : "";
    return {
      firstName: String(allValues.firstName || "").trim(),
      lastName: String(allValues.lastName || "").trim(),
      displayName: [allValues.firstName, allValues.lastName].filter(Boolean).join(" "),
      organizationName,
      type: "CUSTOMER",
    };
  };

  const getWorkspaceCreationPayload = (allValues = form.getFieldsValue(true)) => {
    const draftWorkspace = onboardingDraft?.workspaceCreation || {};
    const accountType = allValues.accountType || "professional";
    const organizationName =
      accountType === "hiring_organization"
        ? String(allValues.organizationName || "").trim()
        : "";
    const onboardingIntent = allValues.helpFirst || accountTypeToIntent(accountType);
    const workspaceUse =
      draftWorkspace.workspaceUse ||
      (onboardingIntent === "find_verified_talent" ? "HIRING" : "JOB_SEARCH");
    const workspaceAccountType =
      draftWorkspace.accountType || (accountType === "hiring_organization" ? "ORGANIZATION" : "PERSONAL");
    const workspaceName =
      draftWorkspace.name ||
      workspaceNameFor({
        accountType: workspaceAccountType,
        organizationName,
        workspaceUse,
        allValues,
      });

    return {
      accountType: workspaceAccountType,
      workspaceUse,
      name: workspaceName,
      organizationId: draftWorkspace.organizationId || undefined,
      organizationName: draftWorkspace.organizationName || organizationName || undefined,
      onboardingIntent,
      product: draftWorkspace.product || "OLIVEEB_OLREADIA_SERVICE",
    };
  };

  const cacheCreatedWorkspace = ({ workspace, actorId, workspaceUse }) => {
    if (!workspace?.id || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(selectedWorkspaceKeyFor(actorId), workspace.id);
      window.localStorage.setItem(globalSelectedWorkspaceKey, workspace.id);
      window.localStorage.setItem(globalSelectedWorkspaceReadyKey, "1");
      window.localStorage.setItem(`selectedWorkspaceUse:${workspace.id}`, workspaceUse || "JOB_SEARCH");
    } catch {
      // Workspace cache is best-effort; the workspace still exists server-side.
    }
  };

  const verifyWorkspaceReady = useCallback(async (workspaceId) => {
    if (!workspaceId) return [];

    let accounts = [];
    for (let attempt = 0; attempt < 10; attempt += 1) {
      accounts = await refreshWorkspaces();
      if (accounts.some((account) => account?.id === workspaceId)) {
        return accounts;
      }
      await wait(700);
    }

    throw new Error("Your workspace is still being prepared. Please wait a moment and try again.");
  }, [refreshWorkspaces]);

  const prepareAccountWorkspace = async ({ signedInUser, workspaceCreation }) => {
    const actorId = signedInUser?.id || signedInUser?.userId || "";
    if (!actorId || !workspaceCreation?.name) {
      clearStarterDraft();
      return null;
    }

    setPreparingAccount(true);
    const existingWorkspacePage = await fetchMemberWorkspaces({
      actorId,
      page: 0,
      size: 1,
    });
    const existingWorkspaces = existingWorkspacePage?.content || [];
    if (existingWorkspaces.length > 0) {
      clearStarterDraft();
      return existingWorkspaces[0];
    }

    const organizationId = workspaceCreation.organizationId || "";

    const workspace = await createWorkspace({
      actorId,
      name: workspaceCreation.name,
      description:
        workspaceCreation.workspaceUse === "HIRING"
          ? "Created from Olreadia hiring onboarding."
          : "Created from Olreadia job search onboarding.",
      accountType: workspaceCreation.accountType || "PERSONAL",
      organizationId: organizationId || undefined,
      workspaceUse: workspaceCreation.workspaceUse,
    });

    if (workspace?.id && workspaceCreation.workspaceUse) {
      try {
        await olreadiaService.workspaceUses.saveForWorkspace({
          actorId,
          workspaceId: workspace.id,
          workspaceUse: workspaceCreation.workspaceUse,
        });
      } catch {
        // Workspace-use creation is best-effort; app defaults still work.
      }
    }

    cacheCreatedWorkspace({
      workspace,
      actorId,
      workspaceUse: workspaceCreation.workspaceUse,
    });
    clearStarterDraft();
    return workspace;
  };

  useEffect(() => {
    if (!pendingWorkspaceSetup || !isAuthenticated) return;
    const signedInUser = user || pendingWorkspaceSetup.signedInUser;
    if (!signedInUser) return;

    let cancelled = false;
    const runWorkspaceSetup = async () => {
      try {
        setPreparingAccount(true);
        const preparedWorkspace = await prepareAccountWorkspace({
          signedInUser,
          workspaceCreation: pendingWorkspaceSetup.workspaceCreation,
        });
        if (preparedWorkspace?.id) {
          selectWorkspace(preparedWorkspace.id);
        }
        await verifyWorkspaceReady(preparedWorkspace?.id);
        if (preparedWorkspace?.id) {
          selectWorkspace(preparedWorkspace.id);
        }
        if (cancelled) return;
        message.success(pendingWorkspaceSetup.successMessage || "Account created successfully.");
        navigate(pendingWorkspaceSetup.redirectTo, { replace: true });
      } catch (error) {
        if (cancelled) return;
        setPreparingAccount(false);
        setWorkspaceSetupBlocked(true);
        const apiMessage =
          error?.response?.data?.humanMessage ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Workspace setup failed. Please try again.";
        setSubmitError(apiMessage);
        message.error(apiMessage);
      } finally {
        if (!cancelled) {
          setPendingWorkspaceSetup(null);
          setLoading(false);
        }
      }
    };

    runWorkspaceSetup();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, navigate, pendingWorkspaceSetup, selectWorkspace, user, verifyWorkspaceReady]);

  const validateCurrentStep = async () => {
    if (currentStep.key === "account") {
      await form.validateFields(["accountType"]);
      if ((form.getFieldValue("accountType") || "professional") === "hiring_organization") {
        await form.validateFields(["organizationName"]);
      }
      return;
    }

    if (currentStep.key === "intent") {
      await form.validateFields(["helpFirst"]);
      return;
    }

    if (currentStep.key === "credentials") {
      await form.validateFields(["email"]);
      if (isSocialSignup) return;
      await form.validateFields(["password", "confirmPassword"]);

      const password = form.getFieldValue("password");
      const confirmPassword = form.getFieldValue("confirmPassword");
      if (password !== confirmPassword) {
        throw new Error("Passwords do not match.");
      }
      return;
    }

    if (currentStep.key === "name") {
      await form.validateFields(["firstName", "lastName"]);
      return;
    }

  };

  const goNext = async () => {
    try {
      await validateCurrentStep();
      setSubmitError("");
      setWorkspaceSetupBlocked(false);
      moveToStep(stepIndex + 1);
    } catch (error) {
      message.error(error?.message || "Please complete the required details.");
    }
  };

  const goBack = () => {
    if (stepIndex === 0) {
      navigate(onboardingDraft ? paths.getStarted : paths.authLanding, { state: location.state });
      return;
    }
    if (stepHistory.length <= 1) {
      navigate(onboardingDraft ? paths.getStarted : paths.authLanding, { state: location.state });
      return;
    }

    const nextHistory = stepHistory.slice(0, -1);
    setStepHistory(nextHistory);
    setStepIndex(nextHistory[nextHistory.length - 1] ?? 0);
  };

  const handleSignup = async () => {
    try {
      await form.validateFields(["accountType", "helpFirst", "email", "firstName", "lastName"]);
      if ((form.getFieldValue("accountType") || "professional") === "hiring_organization") {
        await form.validateFields(["organizationName"]);
      }
      if (!isSocialSignup) {
        await form.validateFields(["password", "confirmPassword"]);
        const password = form.getFieldValue("password");
        const confirmPassword = form.getFieldValue("confirmPassword");
        if (password !== confirmPassword) {
          throw new Error("Passwords do not match.");
        }
      }
      await validateCurrentStep();
      setLoading(true);
      setSubmitError("");
      setWorkspaceSetupBlocked(false);

      const allValues = form.getFieldsValue(true);
      const signupIdentity = getSignupIdentity(allValues);
      const workspaceCreation = getWorkspaceCreationPayload(allValues);
      const hasWorkspaceSetup = Boolean(workspaceCreation?.name);
      clearWorkspaceSelectionCache();
      clearPostLoginRedirect();
      if (isSocialSignup) {
        const body = {
          ...(socialData?.authPayload || {}),
          email: allValues.email?.trim(),
          firstName: signupIdentity.firstName,
          lastName: signupIdentity.lastName,
          username: allValues.email?.trim(),
          isSocial: true,
          type: signupIdentity.type,
          onboardingIntent: allValues.helpFirst || accountTypeToIntent(allValues.accountType),
          accountType: allValues.accountType || "professional",
          currentAppRole: "APP_RESTRICTED_USER",
        };

        if (!body.email) {
          throw new Error(`${providerLabel(socialProvider)} sign-up needs an email address.`);
        }

        const result = await loginWithSocialProvider(socialProvider, body);
        if (!result?.success) {
          throw new Error(result?.error || `${providerLabel(socialProvider)} sign-up failed.`);
        }

        if (hasWorkspaceSetup) {
          setPreparingAccount(true);
          setPendingWorkspaceSetup({
            signedInUser: result.user || result.data,
            workspaceCreation,
            redirectTo: paths.pmDashboard,
            successMessage: `${providerLabel(socialProvider)} account created successfully.`,
          });
          return;
        }
        clearStarterDraft();
        message.success(`${providerLabel(socialProvider)} account created successfully.`);
        navigate(paths.pmDashboard, { replace: true });
        return;
      }

      const payload = {
        firstName: signupIdentity.firstName,
        lastName: signupIdentity.lastName,
        email: allValues.email.trim(),
        username: allValues.email.trim(),
        password: allValues.password,
        isSocial: false,
        type: signupIdentity.type,
        onboardingIntent: allValues.helpFirst || accountTypeToIntent(allValues.accountType),
        accountType: allValues.accountType || "professional",
        currentAppRole: "APP_RESTRICTED_USER",
      };

      await apiClient.post("/identity-service/api/v1/auth/signup", payload, {
        headers: {
          "O-SKIP-WORKSPACE-CONTEXT": "1",
        },
      });

      const loginResult = await login(payload.email, payload.password);
      if (!loginResult?.success) {
        message.success("Account created. Please sign in to continue.");
        navigate(paths.simpleLogin, { replace: true, state: location.state });
        return;
      }

      if (hasWorkspaceSetup) {
        setPreparingAccount(true);
        setPendingWorkspaceSetup({
          signedInUser: loginResult.user || loginResult.data,
          workspaceCreation,
          redirectTo: paths.pmDashboard,
          successMessage: "Account created successfully.",
        });
        return;
      }
      clearStarterDraft();
      message.success("Account created successfully.");
      navigate(paths.pmDashboard, { replace: true });
    } catch (error) {
      setPreparingAccount(false);
      const apiMessage =
        error?.response?.data?.humanMessage ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Signup failed. Please try again.";
      setSubmitError(apiMessage);
      message.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const beginSocialSignup = (provider, authPayload, details = {}, fallbackProfile = {}) => {
    const userInfo = details?.userInfo || fallbackProfile || {};
    const jwtPayload = authPayload?.idToken || authPayload?.identityToken
      ? decodeJwtPayload(authPayload.idToken || authPayload.identityToken)
      : {};
    if (details?.requiresLinkApproval) {
      navigate(paths.socialSetup || paths.appleSetup, {
        state: {
          mode: "link",
          provider,
          authPayload,
          providerUserId:
            details?.providerUserId ||
            details?.googleUserId ||
            details?.appleUserId ||
            details?.microsoftUserId ||
            details?.facebookUserId ||
            details?.linkedinUserId ||
            authPayload?.providerUserId ||
            authPayload?.googleUserId ||
            authPayload?.appleUserId ||
            authPayload?.microsoftUserId ||
            authPayload?.facebookUserId ||
            authPayload?.linkedinUserId ||
            authPayload?.user ||
            jwtPayload?.sub ||
            jwtPayload?.oid ||
            "",
          userInfo,
          message:
            details?.message ||
            `An account already exists with this email. Confirm linking to continue with ${providerLabel(provider)}.`,
          linkingProcessId: details?.linkingProcessId || "",
          requiresPassword: !!details?.requiresPassword,
          requiresLinkApproval: true,
        },
      });
      return;
    }

    const split = splitName(
      userInfo.name ||
      jwtPayload.name ||
      [userInfo.firstName, userInfo.lastName].filter(Boolean).join(" "),
    );
    const firstName =
      userInfo.firstName ||
      userInfo.givenName ||
      jwtPayload.given_name ||
      split.firstName ||
      "";
    const lastName =
      userInfo.lastName ||
      userInfo.familyName ||
      jwtPayload.family_name ||
      split.lastName ||
      "";
    const email =
      userInfo.email ||
      details?.email ||
      authPayload?.email ||
      jwtPayload.email ||
      "";

    setSocialProvider(provider);
    setSocialData({ authPayload, details, userInfo });
    form.setFieldsValue({
      email,
      firstName,
      lastName,
    });
    moveToStep(steps.findIndex((step) => step.key === "name"));
    setSubmitError(
      details?.message ||
      `${providerLabel(provider)} verified. Complete these details to finish your Olreadia account.`,
    );
  };

  const handleGoogleSignup = async (idToken) => {
    try {
      setSocialLoading("google");
      const authPayload = { idToken };
      const jwtPayload = decodeJwtPayload(idToken);
      beginSocialSignup("google", authPayload, {}, {
        email: jwtPayload.email,
        name: jwtPayload.name,
        firstName: jwtPayload.given_name,
        lastName: jwtPayload.family_name,
        picture: jwtPayload.picture,
      });
    } catch (error) {
      message.error(error?.message || "Google sign-up failed.");
    } finally {
      setSocialLoading("");
    }
  };

  const handleAppleSignup = async () => {
    try {
      setSocialLoading("apple");
      await loadAppleSdk();

      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: APPLE_REDIRECT_URI,
        usePopup: true,
        state:
          window.crypto && typeof window.crypto.randomUUID === "function"
            ? window.crypto.randomUUID()
            : `apple-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });

      const appleResponse = await window.AppleID.auth.signIn();
      const { authPayload, profile } = buildAppleAuthPayload(appleResponse);
      beginSocialSignup("apple", authPayload, {}, profile);
    } catch (error) {
      const text = String(error?.message || error?.error || "").toLowerCase();
      if (!text.includes("cancel")) {
        message.error(error?.message || "Apple sign-up failed.");
      }
    } finally {
      setSocialLoading("");
    }
  };

  const handleMicrosoftSignup = async () => {
    try {
      setSocialLoading("microsoft");
      if (!MICROSOFT_CLIENT_ID) throw new Error("Microsoft sign-up is not configured.");

      await loadMicrosoftSdk();

      const msalInstance = new window.msal.PublicClientApplication({
        auth: {
          clientId: MICROSOFT_CLIENT_ID,
          authority: "https://login.microsoftonline.com/common",
          redirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: "sessionStorage",
          storeAuthStateInCookie: false,
        },
      });
      
      let response;
      try {
        response = await msalInstance.loginPopup({
          scopes: ["openid", "profile", "email"],
          prompt: "select_account",
        });
      } catch (popupErr) {
        const errMsg = String(popupErr?.message || popupErr?.errorMessage || "").toLowerCase();
        if (errMsg.includes("popup") || errMsg.includes("window") || errMsg.includes("blocked")) {
          throw new Error("Microsoft sign-up popup was blocked. Please allow popups for this site and try again.");
        }
        throw popupErr;
      }

      console.log("Microsoft signup response:", response);
      const idToken = response?.idToken;
      if (!idToken) {
        console.error("Microsoft signup response:", JSON.stringify(response, null, 2));
        throw new Error("No Microsoft ID token returned. Check browser console for details.");
      }

      const account = response?.account || {};
      const jwtPayload = decodeJwtPayload(idToken);
      const microsoftUserId = account.localAccountId || account.homeAccountId || jwtPayload.oid || jwtPayload.sub || "";
      const authPayload = {
        idToken,
        providerUserId: microsoftUserId,
        microsoftUserId,
      };
      beginSocialSignup("microsoft", authPayload, {}, {
        email: account.username || jwtPayload.email || jwtPayload.preferred_username,
        name: account.name || jwtPayload.name,
        firstName: jwtPayload.given_name,
        lastName: jwtPayload.family_name,
        providerUserId: microsoftUserId,
        microsoftUserId,
      });
    } catch (error) {
      const text = String(error?.message || error?.errorMessage || "").toLowerCase();
      if (!text.includes("user_cancelled") && !text.includes("cancel")) {
        message.error(error?.message || error?.errorMessage || "Microsoft sign-up failed.");
      }
    } finally {
      setSocialLoading("");
    }
  };

  const handleFacebookSignup = async () => {
    try {
      setSocialLoading("facebook");
      await loadFacebookSdk();
      const facebookResponse = await new Promise((resolve, reject) => {
        window.FB.login(
          (response) => {
            if (response?.authResponse?.accessToken) {
              resolve(response);
            } else {
              reject(new Error("Facebook sign-up was cancelled or did not return a token."));
            }
          },
          { scope: "email,public_profile" },
        );
      });

      const facebookProfile = await new Promise((resolve) => {
        window.FB.api(
          "/me",
          { fields: "id,first_name,last_name,name,email,picture" },
          (profile) => resolve(profile || {}),
        );
      });
      const authPayload = { accessToken: facebookResponse.authResponse.accessToken };
      beginSocialSignup("facebook", authPayload, {}, {
        email: facebookProfile.email,
        name: facebookProfile.name,
        firstName: facebookProfile.first_name,
        lastName: facebookProfile.last_name,
        picture: facebookProfile.picture?.data?.url,
      });
    } catch (error) {
      message.error(error?.message || "Facebook sign-up failed.");
    } finally {
      setSocialLoading("");
    }
  };

  const handleLinkedInSignup = async () => {
    try {
      setSocialLoading("linkedin");
      const callbackUrl = `${window.location.origin}/auth/linkedin/callback?next=${encodeURIComponent(
        paths.pmDashboard,
      )}`;
      const { redirectUrl } = await initiateLinkedInWebSignIn(callbackUrl);
      if (!redirectUrl) {
        throw new Error("LinkedIn sign-up URL was not returned.");
      }
      window.location.assign(redirectUrl);
    } catch (error) {
      message.error(error?.message || "LinkedIn sign-up failed.");
    } finally {
      setSocialLoading("");
    }
  };

  if (preparingAccount) {
    return (
      <AuthShell mode="center">
        <Card
          variant="borderless"
          style={{
            width: "min(100%, 420px)",
            borderRadius: 28,
            border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
            background: "linear-gradient(155deg, rgba(255,255,255,0.98) 0%, rgba(246,255,249,0.96) 58%, rgba(255,253,235,0.94) 100%)",
            boxShadow: "0 26px 72px rgba(0, 82, 64, 0.14), inset 0 1px 0 rgba(255,255,255,0.95)",
            textAlign: "center",
          }}
          styles={{ body: { padding: 32 } }}
        >
          <Space direction="vertical" size={18} align="center" style={{ width: "100%" }}>
            <div
              style={{
                padding: "8px 16px",
                borderRadius: 24,
                background: OLIVEEB_COLORS.white,
                border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                boxShadow: "0 12px 24px rgba(39,174,48,0.14)",
              }}
            >
              <AuthBrand centered size={86} />
            </div>
            <Spin size="large" />
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Title level={3} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebGreen, fontWeight: 900 }}>
                Preparing your account
              </Title>
              <Paragraph style={{ margin: 0, color: OLIVEEB_COLORS.oliveebBlue, fontSize: 15 }}>
                Creating your Olreadia workspace and saving your setup.
              </Paragraph>
            </Space>
          </Space>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell mode="center">
      <style>{formLabelStyle}</style>
      <Card
        variant="borderless"
        className="olreadia-onboarding-card"
        style={{
          width: "min(100%, 620px)",
          borderRadius: 28,
          border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
          boxShadow: "0 26px 72px rgba(0, 82, 64, 0.14), inset 0 1px 0 rgba(255,255,255,0.95)",
          background: "linear-gradient(155deg, rgba(255,255,255,0.98) 0%, rgba(246,255,249,0.96) 58%, rgba(255,253,235,0.94) 100%)",
          overflow: "hidden",
        }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={goBack}
              shape="circle"
              style={{
                color: OLIVEEB_COLORS.oliveebBlue,
                borderColor: OLIVEEB_COLORS.oliveebBlue,
                background: OLIVEEB_COLORS.white,
                boxShadow: "0 10px 22px rgba(0,87,184,0.12)",
              }}
            />
            <div
              style={{
                padding: "7px 14px",
                borderRadius: 24,
                background: OLIVEEB_COLORS.white,
                border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                boxShadow: "0 12px 24px rgba(39,174,48,0.14)",
              }}
            >
              <AuthBrand centered size={78} />
            </div>
            <Button
              type="link"
              onClick={() => navigate(paths.simpleLogin, { state: location.state })}
              style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 900, paddingInline: 0 }}
            >
              Sign in
            </Button>
          </div>

          <Progress
            percent={signupProgress}
            showInfo={false}
            strokeColor={OLIVEEB_COLORS.oliveebGreen}
            trailColor={OLIVEEB_COLORS.oliveebGreenSoft}
          />

          <Space direction="vertical" size={6} style={{ width: "100%", textAlign: "center" }}>
            <Text
              style={{
                color: OLIVEEB_COLORS.oliveebGreen,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                fontSize: 11,
              }}
            >
              Create account
            </Text>
            <Title
              level={2}
              style={{
                color: OLIVEEB_COLORS.oliveebGreen,
                margin: 0,
                fontWeight: 900,
                lineHeight: 1.12,
              }}
            >
              {stepTitle}
            </Title>
            <Paragraph
              style={{
                margin: 0,
                color: OLIVEEB_COLORS.oliveebBlue,
                fontSize: 15,
                lineHeight: 1.6,
              }}
            >
              {isSocialSignup && currentStep.key === "name"
                ? `${providerLabel(socialProvider)} verified. Add your name to finish your Olreadia account.`
                : stepReason}
            </Paragraph>
          </Space>

          {submitError ? (
            <Alert
              type="warning"
            showIcon
            message="Signup needs attention"
            description={submitError}
            style={{ borderRadius: 14, padding: "8px 12px" }}
          />
          ) : null}

          <Form
            form={form}
            className="olreadia-signup-form"
            layout="vertical"
            requiredMark={false}
            initialValues={{
              helpFirst: "verified_profile",
              accountType: "professional",
            }}
            size="middle"
          >
            <div style={{ display: currentStep.key === "account" ? "block" : "none" }}>
              <Form.Item
                name="accountType"
                rules={[{ required: true, message: "Choose who you are signing up as" }]}
                style={{ marginBottom: 12 }}
              >
                <Input type="hidden" />
              </Form.Item>
              <div className="olreadia-choice-grid">
                {accountTypeOptions.map((option) => (
                  <ChoiceCard
                    key={option.value}
                    option={option}
                    selected={(values.accountType || "professional") === option.value}
                    onClick={() => {
                      form.setFieldsValue({
                        accountType: option.value,
                        helpFirst: accountTypeToIntent(option.value),
                      });
                    }}
                  />
                ))}
              </div>

              <div style={{ marginTop: 14 }}>
                {selectedAccountType === "hiring_organization" ? (
                  <Form.Item
                    label="Organization name"
                    name="organizationName"
                    style={compactFormItemStyle}
                    rules={[{ required: true, message: "Enter your organization name" }]}
                  >
                    <Input
                      prefix={<TeamOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                      placeholder="Organization or team name"
                      autoComplete="organization"
                      style={compactInputStyle}
                    />
                  </Form.Item>
                ) : null}
              </div>
            </div>

            <div style={{ display: currentStep.key === "intent" ? "block" : "none" }}>
              <Form.Item
                name="helpFirst"
                rules={[{ required: true, message: "Choose how you will use Olreadia first" }]}
                style={{ marginBottom: 0 }}
              >
                <Input type="hidden" />
              </Form.Item>
              <div className="olreadia-choice-grid help">
                {helpOptions.map((option) => (
                  <ChoiceCard
                    key={option.value}
                    option={option}
                    selected={(values.helpFirst || accountTypeToIntent(selectedAccountType)) === option.value}
                    onClick={() => form.setFieldValue("helpFirst", option.value)}
                  />
                ))}
              </div>
            </div>

            <div style={{ display: currentStep.key === "credentials" ? "block" : "none" }}>
              {!socialOnlyCredentialStep ? (
                <Space direction="vertical" size={6} style={{ width: "100%", textAlign: "center", marginBottom: 12 }}>
                  <Text style={{ color: OLIVEEB_COLORS.oliveebDarkBlue, fontWeight: 900 }}>
                    Create your login
                  </Text>
                  <Text style={{ color: OLIVEEB_COLORS.textMuted, fontSize: 12 }}>
                    Use email and password, or continue with a social account.
                  </Text>
                </Space>
              ) : null}

              <div className="auth-social auth-social--minimal olreadia-signup-social">
                <div className="auth-social__card">
                  <GoogleSignupButton
                    disabled={loading || Boolean(socialLoading)}
                    onSuccess={handleGoogleSignup}
                    onError={(error) => message.error(error?.message || "Google sign-up failed.")}
                  />
                  <Button
                    block
                    size="large"
                    className="auth-social__provider auth-social__microsoft"
                    loading={socialLoading === "microsoft"}
                    disabled={loading || Boolean(socialLoading)}
                    icon={<WindowsOutlined />}
                    onClick={handleMicrosoftSignup}
                  >
                    Continue with Microsoft
                  </Button>
                  <Button
                    block
                    size="large"
                    className="auth-social__provider auth-social__linkedin"
                    loading={socialLoading === "linkedin"}
                    disabled={loading || Boolean(socialLoading)}
                    icon={<LinkedInBrandIcon />}
                    onClick={handleLinkedInSignup}
                  >
                    Continue with LinkedIn
                  </Button>
                  <Button
                    block
                    size="large"
                    className="auth-social__apple"
                    loading={socialLoading === "apple"}
                    disabled={loading || Boolean(socialLoading)}
                    icon={<AppleBrandIcon />}
                    onClick={handleAppleSignup}
                    style={{ background: "#000", borderColor: "#000", color: "#fff" }}
                  >
                    Continue with Apple
                  </Button>
                </div>
              </div>

              {!socialOnlyCredentialStep ? (
                <>
                  <Divider plain style={{ margin: "10px 0 8px", fontSize: 11 }}>
                    or create with email
                  </Divider>

                  <Form.Item
                    label="Username"
                    name="email"
                    style={compactFormItemStyle}
                    rules={[
                      { required: true, message: "Enter your username" },
                      { type: "email", message: "Enter a valid email address" },
                    ]}
                  >
                    <Input
                      prefix={<MailOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                      placeholder="you@company.com"
                      autoComplete="email"
                      style={compactInputStyle}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Password"
                    name="password"
                    style={compactFormItemStyle}
                    rules={[
                      { required: true, message: "Enter a password" },
                      { min: 8, message: "Password must be at least 8 characters" },
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                      placeholder="Create a password"
                      autoComplete="new-password"
                      style={compactInputStyle}
                      iconRender={(visible) => visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Confirm password"
                    name="confirmPassword"
                    dependencies={["password"]}
                    style={compactFormItemStyle}
                    rules={[
                      { required: true, message: "Confirm your password" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Passwords do not match"));
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                      placeholder="Confirm password"
                      autoComplete="new-password"
                      style={compactInputStyle}
                      iconRender={(visible) => visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    />
                  </Form.Item>
                </>
              ) : null}
            </div>

            <div style={{ display: currentStep.key === "name" ? "block" : "none" }}>
              <div className="olreadia-choice-grid">
                <Form.Item
                  label="First name"
                  name="firstName"
                  style={compactFormItemStyle}
                  rules={[{ required: true, message: "Enter your first name" }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    autoComplete="given-name"
                    style={compactInputStyle}
                  />
                </Form.Item>
                <Form.Item
                  label="Last name"
                  name="lastName"
                  style={compactFormItemStyle}
                  rules={[{ required: true, message: "Enter your last name" }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    autoComplete="family-name"
                    style={compactInputStyle}
                  />
                </Form.Item>
              </div>
            </div>

          </Form>

          <Space direction="vertical" size={6} style={{ width: "100%" }}>
            {socialOnlyCredentialStep ? null : currentStep.key === "name" ? (
              <Button
                type="primary"
                size="large"
                block
                icon={<CheckCircleOutlined />}
                loading={loading}
                onClick={handleSignup}
                style={primaryActionStyle}
              >
                Create account
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                block
                onClick={goNext}
                style={primaryActionStyle}
              >
                Continue
              </Button>
            )}
          </Space>
        </Space>
      </Card>
    </AuthShell>
  );
};

export default AuthSignup;
